import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  Alert,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MultiInputTimeRangeField } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { buttonStyle, themeStyles } from "../../constants";

const modifiers = [
  { label: "HN", value: "HN" },
  { label: "HO", value: "HO" },
  // Add other modifiers as needed
];

const diagnosisCodes = [
  { label: "F84.0", value: "F84.0" },
  { label: "F84.1", value: "F84.1" },
  { label: "Other", value: "Other" },
];

const AddSessionNote = ({ open, onClose, onSave, initialValues, clients }) => {
  //console.log("Initial values:", initialValues);
  const [formValues, setFormValues] = useState({
    ...initialValues,
    sessionStartTime: null,
    sessionEndTime: null,
  });
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");

  useEffect(() => {
    //console.log("Initial values:", initialValues);
    setFormValues((prevValues) => ({
      ...prevValues,
      sessionStartTime: null,
      sessionEndTime: null,
    }));
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "insuranceName" && /[^a-zA-Z\s]/.test(value)) {
      return; // Prevent non-text characters in insuranceName
    } else if (name === "policyNumber" && /[^a-zA-Z0-9\s]/.test(value)) {
      return; // Prevent non-alphanumeric characters in policyNumber
    }
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleDateChange = (date, fieldName) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: date,
    }));
  };

  const handleTimeRangeChange = (newValue) => {
    if (newValue[0] && newValue[1] && newValue[1].isBefore(newValue[0])) {
      setAlert("Session end time cannot be before session start time");
      setErrors((prevErrors) => ({
        ...prevErrors,
        sessionTime: "Session end time cannot be before session start time",
      }));
    } else {
      setAlert("");
      setErrors((prevErrors) => ({
        ...prevErrors,
        sessionTime: "",
      }));
      setFormValues((prevValues) => ({
        ...prevValues,
        sessionStartTime: newValue[0],
        sessionEndTime: newValue[1],
      }));
    }
  };

  const handleAutocompleteChange = (event, newValue, fieldName) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: newValue?.value || "",
    }));
  };

  const handleSave = () => {
    const newErrors = {};
    if (!formValues.sessionDate)
      newErrors.sessionDate = "Session date is required";
    if (!formValues.sessionStartTime || !formValues.sessionEndTime)
      newErrors.sessionTime = "Session time is required";
    if (!formValues.modifier) newErrors.modifier = "Modifier is required";
    if (!formValues.diagnosisCode1)
      newErrors.diagnosisCode1 = "Diagnosis code is required";
    if (formValues.diagnosisCode1 === "Other" && !formValues.otherDiagnosisCode)
      newErrors.otherDiagnosisCode = "Other diagnosis code is required";
    if (!formValues.insuranceName)
      newErrors.insuranceName = "Insurance name is required";
    if (!formValues.policyNumber)
      newErrors.policyNumber = "Policy number is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const formattedValues = {
      ...formValues,
      sessionDate: formValues.sessionDate
        ? dayjs(formValues.sessionDate).format("YYYY-MM-DD")
        : "",
      sessionStartTime: formValues.sessionStartTime
        ? dayjs(formValues.sessionStartTime).format("hh:mm A")
        : "",
      sessionEndTime: formValues.sessionEndTime
        ? dayjs(formValues.sessionEndTime).format("hh:mm A")
        : "",
    };
    //console.log(formattedValues, "details");

    onSave(formattedValues);
  };

  const handleDiagnosisCodeChange = (event, newValue, fieldName) => {
    handleAutocompleteChange(event, newValue, fieldName);
    if (fieldName === "diagnosisCode1") {
      setFormValues((prevValues) => ({
        ...prevValues,
        otherDiagnosisCode:
          newValue?.value === "Other" ? "" : prevValues.otherDiagnosisCode,
      }));
    }
  };

  const isSaveDisabled =
    !formValues.sessionDate ||
    !formValues.sessionStartTime ||
    !formValues.sessionEndTime ||
    !formValues.modifier ||
    !formValues.diagnosisCode1 ||
    (formValues.diagnosisCode1 === "Other" && !formValues.otherDiagnosisCode) ||
    !formValues.insuranceName ||
    !formValues.policyNumber ||
    (formValues.sessionStartTime &&
      formValues.sessionEndTime &&
      formValues.sessionEndTime.isBefore(formValues.sessionStartTime));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#000",
            color: "#fff",
            boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
            borderRadius: "10px",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "#ffa500",
            fontWeight: "bold",
          }}
        >
          Session Details
        </DialogTitle>
        <DialogContent>
          {alert && <Alert severity="error">{alert}</Alert>}
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ marginTop: 1 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Session Date"
                  value={
                    formValues.sessionDate
                      ? dayjs(formValues.sessionDate)
                      : null
                  }
                  onChange={(newValue) =>
                    handleDateChange(newValue, "sessionDate")
                  }
                  maxDate={dayjs()}
                  sx={themeStyles.textField}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      margin="normal"
                      error={!!errors.sessionDate}
                      helperText={errors.sessionDate}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 50,
                        style: { color: "#fff" }, // Change text color to white
                      }}
                      sx={themeStyles.textField}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MultiInputTimeRangeField
                  value={[
                    formValues.sessionStartTime
                      ? dayjs(formValues.sessionStartTime)
                      : null,
                    formValues.sessionEndTime
                      ? dayjs(formValues.sessionEndTime)
                      : null,
                  ]}
                  onChange={handleTimeRangeChange}
                  sx={themeStyles.textField}
                  renderInput={(startProps, endProps) => (
                    <>
                      <TextField
                        {...startProps}
                        fullWidth
                        margin="normal"
                        error={!!errors.sessionTime}
                        helperText={errors.sessionTime}
                        inputProps={{
                          ...startProps.inputProps,
                          maxLength: 50,
                          style: { color: "#fff" }, // Change text color to white
                        }}
                      />
                      <Box sx={{ mx: 1 }}> to </Box>
                      <TextField
                        {...endProps}
                        fullWidth
                        margin="normal"
                        error={!!errors.sessionTime}
                        helperText={errors.sessionTime}
                        inputProps={{
                          ...endProps.inputProps,
                          maxLength: 50,
                          // Change text color to white
                        }}
                      />
                    </>
                  )}
                  slotProps={{
                    textField: ({ position }) => ({
                      label:
                        position === "start" ? "Session start" : "Session end",
                      required: true,
                    }),
                  }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={modifiers}
                  getOptionLabel={(option) => option.label}
                  value={
                    modifiers?.find(
                      (option) => option.value === formValues.modifier
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    handleAutocompleteChange(event, newValue, "modifier")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Modifier"
                      margin="normal"
                      error={!!errors.modifier}
                      helperText={errors.modifier}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 50,
                      }}
                      sx={themeStyles.textField}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={diagnosisCodes}
                  getOptionLabel={(option) => option.label}
                  value={
                    diagnosisCodes?.find(
                      (option) => option.value === formValues.diagnosisCode1
                    ) || {
                      label: formValues.diagnosisCode1,
                      value: formValues.diagnosisCode1,
                    }
                  }
                  onChange={(event, newValue) =>
                    handleDiagnosisCodeChange(event, newValue, "diagnosisCode1")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      label="Diagnosis Code"
                      margin="normal"
                      error={!!errors.diagnosisCode1}
                      helperText={errors.diagnosisCode1}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 50,
                        style: { color: "#fff" }, // Change text color to white
                      }}
                      sx={themeStyles.textField}
                    />
                  )}
                />
              </Grid>
              {formValues.diagnosisCode1 === "Other" && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Other Diagnosis Code"
                    name="otherDiagnosisCode"
                    value={formValues.otherDiagnosisCode}
                    onChange={handleChange}
                    margin="normal"
                    error={!!errors.otherDiagnosisCode}
                    helperText={errors.otherDiagnosisCode}
                    inputProps={{
                      maxLength: 50,
                      style: { color: "#fff" }, // Change text color to white
                    }}
                    sx={themeStyles.textField}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Name*"
                  name="insuranceName"
                  value={formValues.insuranceName}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.insuranceName}
                  helperText={errors.insuranceName}
                  inputProps={{
                    maxLength: 50,
                    style: { color: "#fff" }, // Change text color to white
                  }}
                  sx={themeStyles.textField}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Policy Number*"
                  name="policyNumber"
                  value={formValues.policyNumber}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.policyNumber}
                  helperText={errors.policyNumber}
                  inputProps={{
                    maxLength: 50,
                    style: { color: "#fff" }, // Change text color to white
                  }}
                  sx={themeStyles.textField}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": { backgroundColor: "#b71c1c" },
              padding: "10px 20px",
              fontWeight: "bold",
            }}
            startIcon={<CloseIcon />}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              backgroundColor: "#ffa500",
              color: "#fff",
              "&:hover": { backgroundColor: "#d0892f" },
              padding: "10px 20px",
              fontWeight: "bold",
            }}
            startIcon={<SaveIcon />}
            fullWidth
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AddSessionNote;
